// ============================================================
// Stocora — Permission catalog (Phase 3, Epic 3D.1)
// ============================================================
// Single source of truth for the named permissions used by
// hasPermission() across the PWA and Edge Functions. Each entry
// pairs a permission name with the default roles that satisfy
// it; the SQL function in
//   20260508150000_permission_resolver_foundation.sql
//   20260509000000_permission_resolver_exports.sql
//   20260511120000_permission_view_as_role.sql
//   20260512120000_review_queue_3a6.sql
//   20260513120000_reconciliation_cycles.sql
//   20260515120000_permission_posture_realignment.sql
//   20260519120000_par_edit_and_enum_sweep.sql
//   20260521120000_audit_detection_4b1_4b3.sql
//   20260524120000_audit_actions_4b6.sql
// mirrors these defaults via CASE.
//
// Adding a permission:
//   1. Add the entry here.
//   2. Add the matching CASE branch in has_permission() in a
//      new migration.
//   3. Touch the call site (Edge Function or PWA component).
//
// Forgetting (1) is caught by the literal-string union — calls
// to hasPermission() with an unknown name don't typecheck.
// Forgetting (2) is caught at runtime — has_permission() falls
// through to `else false` for unknown names. Drift between this
// file and the SQL CASE is reviewed by inspection until a test
// runner lands (deferred follow-up).
//
// Posture policy (locked 2026-05-12, applied retroactively to
// existing entries in 3D.3 on 2026-05-15, see DECISIONS.md):
// the catalog default for a new permission is any_member (all
// three roles), unless one of:
//   (a) the action is a spending commitment (reorder.approve,
//       reorder.approval_email.generate, par.edit — par drives
//       reorder enqueue)
//   (b) the action is a safety/impersonation axis
//       (system.view_as_role)
//   (c) the action exposes financial or sensitive data the
//       clinic might reasonably want gated by default
//       (exports.generate — judgment call, revisitable)
// ============================================================

import type { ClinicRole } from '@/lib/session';

export interface PermissionEntry {
  /** Stable name. Mirror in has_permission() CASE branch. */
  readonly name: string;
  /** One-line semantic. Used by PermissionRequiredBanner's
   *  fallback message and any future admin UI. */
  readonly description: string;
  /** Roles that satisfy the permission by default. Future 7D
   *  grants table can override per-clinic / per-user / etc. */
  readonly defaultRoles: readonly ClinicRole[];
}

export const PERMISSION_CATALOG = {
  // ---- Reorder workflow --------------------------------------
  // draft.edit is any_member (3D.3) — editing a draft pre-
  // approval is stock-admin work, not spending. approve and
  // approval_email.generate stay manager+ as spending commits.
  'reorder.draft.edit': {
    name: 'reorder.draft.edit',
    description:
      'Edit quantities and remove items from a pending reorder draft.',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'reorder.approve': {
    name: 'reorder.approve',
    description:
      'Approve a reorder batch and send the PO to the vendor.',
    defaultRoles: ['owner', 'manager'],
  },
  'reorder.approval_email.generate': {
    name: 'reorder.approval_email.generate',
    description:
      'Trigger the approval email send for pending reorders.',
    defaultRoles: ['owner', 'manager'],
  },

  // ---- Inbound email ----------------------------------------
  // All five flipped to any_member (3D.3) — parser invocation
  // and review-queue work are stock-admin surfaces. The
  // BottomNav Review tab is therefore visible to all roles.
  'inbound_email.parse.invoke': {
    name: 'inbound_email.parse.invoke',
    description:
      'Manually invoke the vendor email parser on an inbound email.',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'inbound_email.review_queue.view': {
    name: 'inbound_email.review_queue.view',
    description:
      'View the inbound email review queue (low-confidence parses, no-match dispatches, type-tagged signals).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'inbound_email.review_queue.edit': {
    name: 'inbound_email.review_queue.edit',
    description:
      'Edit a parsed email payload in the review queue detail view (qty / sku / vendor / email_type / order_references).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'inbound_email.review_queue.commit': {
    name: 'inbound_email.review_queue.commit',
    description:
      'Commit reviewer edits and re-dispatch via the matcher with force=true.',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'inbound_email.review_queue.dismiss': {
    name: 'inbound_email.review_queue.dismiss',
    description:
      'Dismiss a row from the review queue (retained for audit, hidden from queue views).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },

  // ---- Reconciliation (3B; posture-aligned any_member) ------
  // First permissions to land under the 2026-05-12 posture
  // policy. Stock-handling work, not spending.
  'reconciliation.start': {
    name: 'reconciliation.start',
    description:
      'Open a new reconciliation cycle (snapshots current essential×non-dead-stock counts as baseline).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'reconciliation.submit_count': {
    name: 'reconciliation.submit_count',
    description:
      'Enter per-item counts, skip items, and finalize a cycle (writes inferred_usage events to the inventory record).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'reconciliation.view_history': {
    name: 'reconciliation.view_history',
    description:
      'View past reconciliation cycles and their per-item deltas.',
    defaultRoles: ['owner', 'manager', 'staff'],
  },

  // ---- Inventory editing (4A.3a, 2026-05-19) ----------------
  // Par changes drive reorder enqueue: raising par puts the
  // product below the new par from the resolver's perspective
  // and the existing enqueue_below_par_reorder trigger drops
  // a pending reorder_queue row. That makes par.edit a
  // spending lever, which aligns with the 2026-05-12 posture
  // policy's spending-axis carve-out. The 2026-05-12 entry
  // forward-flagged this exact decision ("Future 4A par.edit
  // is judgment ... lean toward manager+ but revisit at 4A.3
  // time"). Revisited at 4A.3a, kept manager+.
  'par.edit': {
    name: 'par.edit',
    description:
      'Adjust a product\u2019s par level (the count at or below which a reorder is auto-enqueued).',
    defaultRoles: ['owner', 'manager'],
  },

  // ---- Audit (4B.1 + 4B.3, 2026-05-22) ----------------------
  // Both any_member per posture policy. audit.scan.invoke
  // gates the on-demand AuditPage "Scan now" button (the
  // nightly cron path bypasses since it runs as postgres).
  // audit.view gates the page itself; the underlying RLS
  // SELECT policy on audit_findings is already is_clinic_member,
  // so the named permission is a chrome-level gate rather
  // than data-isolating — useful for future per-clinic
  // tightening via 7D grants. Dismiss / resolve actions ship
  // in 4B.6 below.
  'audit.scan.invoke': {
    name: 'audit.scan.invoke',
    description:
      'Trigger an on-demand audit detection scan (dead-stock + over-par) for the active clinic.',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'audit.view': {
    name: 'audit.view',
    description:
      'View the audit findings page (dead-stock, over-par, mismatch, expiring-soon detections).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },

  // ---- Audit actions (4B.6, 2026-05-24) ---------------------
  // Both any_member per posture policy. Resolve actions
  // include dispose (write-off, mutates inventory) — under
  // posture this is stock-handling work, not a spending
  // commit, so any_member is correct. Splitting dismiss from
  // resolve gives 7D a per-clinic tightening surface: a
  // clinic could grant dismiss to all staff but restrict
  // resolve to managers via permission_grants.
  'audit.finding.dismiss': {
    name: 'audit.finding.dismiss',
    description:
      'Close an audit finding without side effects (per-finding; no cascade).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },
  'audit.finding.resolve': {
    name: 'audit.finding.resolve',
    description:
      'Resolve an audit finding with side effects: stop reordering, dispose (write-off), or use-it-now. Cascades to sibling open findings on the same product.',
    defaultRoles: ['owner', 'manager', 'staff'],
  },

  // ---- Audit "Email rep" (Block C, 2026-07-14) --------------
  // any_member per posture policy. The action sends a buyback/
  // return/credit INQUIRY to the vendor's sales rep — a
  // no-commitment ask, strictly less consequential than dispose
  // (which is also any_member under resolve). Moved from a
  // client-only mailto: to a server-side send (send-rep-email);
  // the send is now permission-gated, so it needs this entry +
  // the matching has_permission() branch (20260714140000).
  'audit.finding.email_rep': {
    name: 'audit.finding.email_rep',
    description:
      'Email a vendor sales rep a buyback/return/credit inquiry about a flagged item (sent server-side as the clinic).',
    defaultRoles: ['owner', 'manager', 'staff'],
  },

  // ---- Exports ----------------------------------------------
  // Stays manager+ — financial-data axis (judgment call per
  // posture policy). Revisit if pilot signal pushes either way.
  'exports.generate': {
    name: 'exports.generate',
    description:
      'Generate exports and financial reports (Phase 5: QuickBooks CSV, monthly PDF, savings).',
    defaultRoles: ['owner', 'manager'],
  },

  // ---- Clinic settings / Practice Profile (Block A, 2026-07-14)
  // Sensitive-financial/config axis, not any_member: the
  // purchasing contact feeds the vendor-facing PO reply-to
  // (Block B) and the thresholds shape the audit's $-figures
  // (Block D). Mirrors the has_permission() CASE branch added
  // in 20260714120000_clinic_settings_and_change_log.sql — the
  // two must change together (atomic-permission invariant).
  'clinic.settings.edit': {
    name: 'clinic.settings.edit',
    description:
      'Edit the clinic Practice Profile (purchasing email, phone, hours) and audit thresholds (dead-stock, expiring windows).',
    defaultRoles: ['owner', 'manager'],
  },

  // ---- Clinic members (2026-07-19) ---------------------------
  // Owner-only. Gates the invite-clinic-member Edge Function (adds
  // a teammate + assigns their role). Safety/impersonation-adjacent
  // axis per posture policy, not stock-admin work. Role changes and
  // removal of EXISTING members are not gated by this permission —
  // they're direct clinic_members writes, already owner-only via
  // RLS (clinic_members_insert/update/delete). Mirrors the RLS
  // (rather than has_permission) call rationale of vendors_write.
  'clinic.member.invite': {
    name: 'clinic.member.invite',
    description:
      'Invite a new teammate by email and assign their clinic role, or attach an existing user to this clinic.',
    defaultRoles: ['owner'],
  },

  // ---- System / dev tooling ---------------------------------
  // Stays owner-only — impersonation safety axis. PWA-only
  // consumer today. SQL branch exists for catalog parity; no
  // Edge Function reads it.
  'system.view_as_role': {
    name: 'system.view_as_role',
    description:
      'Impersonate a lower-privilege role in the PWA for demos and gate verification (actions still execute with your real role).',
    defaultRoles: ['owner'],
  },
} as const satisfies Record<string, PermissionEntry>;

export type PermissionName = keyof typeof PERMISSION_CATALOG;

/**
 * Compute the set of permission names a given role satisfies by
 * default. Used at session-load time in App.tsx to populate the
 * sync PWA permission cache on SessionValue.
 *
 * When 7D's permission_grants table lands, App.tsx swaps this
 * call for a one-time fetch that materializes the same shape;
 * the cache contract (ReadonlySet<PermissionName>) is unchanged.
 *
 * Note on the `defaultRoles` cast: the `as const satisfies`
 * pattern on PERMISSION_CATALOG narrows each entry's
 * defaultRoles to a literal tuple (e.g. readonly ['owner',
 * 'manager']) rather than readonly ClinicRole[]. That's what
 * makes `keyof typeof PERMISSION_CATALOG` resolve to a literal
 * permission-name union — load-bearing for hasPermission()
 * call-site type safety. The trade-off: Array.includes() on
 * those narrowed tuples rejects ClinicRole values that don't
 * appear in the tuple (used to reject 'staff' on now-any_member
 * entries; still rejects 'staff' on the few remaining
 * manager+ entries). We widen at the one call site rather
 * than relax the catalog typing.
 */
export function defaultPermissionsForRole(
  role: ClinicRole,
): ReadonlySet<PermissionName> {
  const out = new Set<PermissionName>();
  for (const [name, entry] of Object.entries(PERMISSION_CATALOG)) {
    if ((entry.defaultRoles as readonly ClinicRole[]).includes(role)) {
      out.add(name as PermissionName);
    }
  }
  return out;
}
